import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaView1Service, serviceName } from '../service';
import { GetLocationsSourceInfoIO, ServiceQueries } from '../types';
import { useView1Queries } from './use-view1-queries';
import { UseView1QueryEndpointArgs, useView1Query } from './use-view1-query';

/**
 * A hook that returns a query for the `GetLocationsSourceInfo` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetLocationsSourceInfoQuery = <E = unknown, D = GetLocationsSourceInfoIO['output']>(
  args: UseView1QueryEndpointArgs<'GetLocationsSourceInfo', E, D>
) =>
  useView1Query({
    endpointName: 'GetLocationsSourceInfo',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetLocationsSourceInfo` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetLocationsSourceInfoQueries = <E = unknown, D = GetLocationsSourceInfoIO['output']>(
  argsArr: UseView1QueryEndpointArgs<'GetLocationsSourceInfo', E, D>[]
) =>
  useView1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetLocationsSourceInfo',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetLocationsSourceInfo` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetLocationsSourceInfo = () => {
  const queryClient = useQueryClient();

  const fetchGetLocationsSourceInfo = async <E = unknown, D = GetLocationsSourceInfoIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseView1QueryEndpointArgs<'GetLocationsSourceInfo', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetLocationsSourceInfoIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetLocationsSourceInfo'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetLocationsSourceInfoIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetLocationsSourceInfo'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetLocationsSourceInfo'>({
          serviceName,
          endpointName: 'GetLocationsSourceInfo',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaView1Service.GetLocationsSourceInfo(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetLocationsSourceInfo;
};
